import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

const CONSENT_KEY = "movi_analytics_consent";

const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return "tablet";
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return "mobile";
  return "desktop";
};

const getTodayDate = (): string => {
  return new Date().toISOString().split("T")[0];
};

// Generate a random anonymous session ID for the day
const getAnonymousId = (): string => {
  const today = getTodayDate();
  const storageKey = `movi_anon_id_${today}`;
  
  let anonId = sessionStorage.getItem(storageKey);
  if (!anonId) {
    anonId = `anon_${today}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem(storageKey, anonId);
  }
  return anonId;
};

export const usePageTracking = () => {
  const location = useLocation();
  const trackedRef = useRef<string | null>(null);

  useEffect(() => {
    const trackVisitor = async () => {
      // Don't track admin or privacy pages
      if (location.pathname.startsWith("/admin") || location.pathname === "/privacy") return;
      
      // Check for consent
      const consent = localStorage.getItem(CONSENT_KEY);
      if (consent !== "true") return;
      
      const today = getTodayDate();
      const anonId = getAnonymousId();
      const sessionKey = `${today}-${anonId}`;
      
      // Prevent duplicate tracking in same session
      if (trackedRef.current === sessionKey) return;
      trackedRef.current = sessionKey;

      try {
        // Route through edge function for validated, rate-limited tracking
        await supabase.functions.invoke('track-page-view', {
          body: {
            session_id: anonId,
            device_type: getDeviceType(),
            path: "/",
            visit_date: today,
          },
        });
      } catch (error) {
        // Silently fail - don't break the user experience
        console.error("Failed to track visitor:", error);
      }
    };

    trackVisitor();
  }, [location.pathname]);
};
